import { useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { Calendar as CalendarIcon, Clock as ClockIcon } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { createBooking } from "@/lib/api";
import { trackBookingSubmitted } from "@/lib/analytics";

const ROLES = ["Architect", "Interior Designer", "Builder", "Homeowner"];
// Studio hours: Mon–Fri 9–5, Sat by appointment, Sun closed. Slots in 30-min intervals.
const TIME_SLOTS = [
  "9:00 AM", "9:30 AM", "10:00 AM", "10:30 AM", "11:00 AM", "11:30 AM",
  "12:00 PM", "12:30 PM", "1:00 PM", "1:30 PM", "2:00 PM", "2:30 PM",
  "3:00 PM", "3:30 PM", "4:00 PM", "4:30 PM", "5:00 PM",
];

export default function BookingForm({ onDone, prefix = "booking" }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [role, setRole] = useState("Homeowner");
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      await createBooking({ ...form, role, date: date ? format(date, "PPP") : null, time: time || null });
      trackBookingSubmitted({ role, date, time, message: form.message });
      toast.success("Request received", { description: "Our studio team will confirm your consultation shortly." });
      setForm({ name: "", email: "", phone: "", message: "" });
      setRole("Homeowner");
      setDate(null);
      setTime("");
      setTimeout(() => onDone?.(), 700);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid={`${prefix}-form`} className="space-y-7">
      <div className="grid sm:grid-cols-2 gap-6">
        <input data-testid={`${prefix}-name`} className="field-underline" placeholder="Full name" value={form.name} onChange={set("name")} />
        <input data-testid={`${prefix}-email`} type="email" className="field-underline" placeholder="Email address" value={form.email} onChange={set("email")} />
      </div>
      <div className="grid sm:grid-cols-2 gap-6">
        <input data-testid={`${prefix}-phone`} className="field-underline" placeholder="Phone (optional)" value={form.phone} onChange={set("phone")} />
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger data-testid={`${prefix}-role`} className="field-underline rounded-none border-0 border-b border-[#D6D1CA]/25 bg-transparent text-[#D6D1CA] focus:ring-0 h-auto px-0.5 py-2.5">
            <SelectValue placeholder="I am a…" />
          </SelectTrigger>
          <SelectContent className="bg-[#1b1c1f] border-[#8C7355]/25 text-[#F5F4F0]">
            {ROLES.map((r) => (
              <SelectItem key={r} value={r} className="focus:bg-[#8C7355]/20 focus:text-[#F5F4F0]">{r}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid sm:grid-cols-2 gap-6">
        <Popover>
          <PopoverTrigger asChild>
            <button type="button" data-testid={`${prefix}-date`} className="field-underline flex items-center justify-between text-left">
              <span className={date ? "text-[#F5F4F0]" : "text-[#D6D1CA]/35"}>{date ? format(date, "PPP") : "Preferred date (optional)"}</span>
              <CalendarIcon size={16} className="text-[#C9A876]" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 bg-[#1b1c1f] border-[#8C7355]/25" align="start">
            <Calendar
              mode="single"
              selected={date}
              onSelect={setDate}
              disabled={(d) => {
                const today = new Date(); today.setHours(0, 0, 0, 0);
                // Block past dates AND Sundays (studio is closed).
                return d < today || d.getDay() === 0;
              }}
              initialFocus
              className="text-[#F5F4F0]"
            />
          </PopoverContent>
        </Popover>

        <Select value={time} onValueChange={setTime}>
          <SelectTrigger data-testid={`${prefix}-time`} className="field-underline rounded-none border-0 border-b border-[#D6D1CA]/25 bg-transparent text-[#F5F4F0] focus:ring-0 h-auto px-0.5 py-2.5 [&>span]:flex [&>span]:items-center [&>span]:gap-2 data-[placeholder]:text-[#D6D1CA]/35">
            <SelectValue placeholder="Preferred time (optional)" />
            <ClockIcon size={16} className="text-[#C9A876] ml-2 shrink-0" />
          </SelectTrigger>
          <SelectContent className="bg-[#1b1c1f] border-[#8C7355]/25 text-[#F5F4F0]">
            {TIME_SLOTS.map((t) => (
              <SelectItem key={t} value={t} className="focus:bg-[#8C7355]/20 focus:text-[#F5F4F0]">{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <textarea data-testid={`${prefix}-message`} rows={2} className="field-underline resize-none" placeholder="Tell us about your project (optional)" value={form.message} onChange={set("message")} />

      <button type="submit" disabled={loading} data-testid={`${prefix}-submit`} className="btn-bronze w-full sm:w-auto rounded-full px-10 py-3.5 text-[11px] tracking-[0.2em] uppercase font-medium disabled:opacity-60">
        {loading ? "Sending…" : "Request Appointment"}
      </button>
    </form>
  );
}
